import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ApiService } from '../../services/api.service';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { InputSwitchModule } from 'primeng/inputswitch';
import { FormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { MultiSelectModule } from 'primeng/multiselect';
import { DropdownModule } from 'primeng/dropdown';
import { ProjectType, Region } from '../../models/api.models';
import { forkJoin } from 'rxjs';

@Component({
  selector: 'app-user-list',
  standalone: true,
  imports: [CommonModule, TableModule, TagModule, InputSwitchModule, FormsModule, ToastModule, DialogModule, ButtonModule, InputTextModule, ConfirmDialogModule, MultiSelectModule, DropdownModule],
  providers: [MessageService, ConfirmationService],
  templateUrl: './user-list.component.html',
  styleUrl: './user-list.component.scss'
})
export class UserListComponent implements OnInit {
  users: any[] = [];
  loading = false;
  error: string | null = null;
  
  displayEditDialog: boolean = false;
  displayRolesDialog: boolean = false;
  displayAddDialog: boolean = false;
  displayDetailDialog: boolean = false;
  selectedUser: any = null;
  userDetails: any = null;
  
  // Role management
  availableRoles: any[] = [];
  userRoles: string[] = [];
  newRolesToAssign: string[] = [];
  selectedClient: ProjectType = ProjectType.SVN;
  projectTypes = [
    { label: 'SVN', value: ProjectType.SVN },
    { label: 'SOVRN', value: ProjectType.SOVRN },
    { label: 'CAPITAL', value: ProjectType.CAPITAL }
  ];

  regions = [
    { label: 'INDIA', value: Region.INDIA },
    { label: 'US', value: Region.US }
  ];
  
  editUserData: any = {
    Username: '',
    Email: '',
    FirstName: '',
    LastName: '',
    Enabled: true
  };

  addUserData: any = {
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    region: Region.INDIA,
    isActive: true
  };

  saving: boolean = false;

  constructor(
    private apiService: ApiService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    this.loadUsers();
  }

  loadUsers(): void {
    this.loading = true;
    this.error = null;
    this.apiService.listUsers().subscribe({
      next: (response) => {
        console.log('API Response:', response);
        // Handle different response structures
        let rawUsers = [];
        if (response && response.data && Array.isArray(response.data)) {
          rawUsers = response.data;
        } else if (Array.isArray(response)) {
          rawUsers = response;
        } else if (response && response.users && Array.isArray(response.users)) {
          rawUsers = response.users;
        }
        
        // Initialize roles property for each user
        this.users = rawUsers.map((u: any) => ({ ...u, roles: [] }));
        this.loading = false;
        
        // Optionally fetch roles for the first few users to show in table
        this.fetchRolesForVisibleUsers();
      },
      error: (err) => {
        this.error = `Failed to load users: ${err.error?.detail || err.message || 'Unknown error'}`;
        this.loading = false;
        console.error('API Error:', err);
      }
    });
  }

  fetchRolesForVisibleUsers(): void {
    // To avoid overloading, we only fetch roles for the first 10 users initially
    // In a real app, you might do this on scroll or pagination
    const limit = Math.min(this.users.length, 10);
    for (let i = 0; i < limit; i++) {
      const user = this.users[i];
      this.apiService.getUserRoles(user.id, this.selectedClient).subscribe({
        next: (res) => {
          user.roles = res && res.data ? res.data.map((r: any) => r.roleName || r) : [];
        }
      });
    }
  }

  manageRoles(user: any): void {
    this.selectedUser = user;
    this.displayRolesDialog = true;
    this.userRoles = [];
    this.newRolesToAssign = [];
    this.loadAvailableRoles();
    this.loadUserRoles();
  }

  loadAvailableRoles(): void {
    this.apiService.getRolesByClient(this.selectedClient).subscribe({
      next: (response) => {
        // Assuming response structure contains role names
        this.availableRoles = response && response.data ? response.data.map((r: any) => ({ label: r.roleName, value: r.roleName })) : [];
      },
      error: (err) => {
        console.error('Failed to load available roles', err);
      }
    });
  }

  loadUserRoles(): void {
    this.apiService.getUserRoles(this.selectedUser.id, this.selectedClient).subscribe({
      next: (response) => {
        // Assuming response is an array of role names or objects
        this.userRoles = response && response.data ? response.data.map((r: any) => r.roleName || r) : [];
      },
      error: (err) => {
        console.error('Failed to load user roles', err);
      }
    });
  }

  saveRoles(): void {
    if (this.newRolesToAssign.length === 0) {
      this.displayRolesDialog = false;
      return;
    }

    this.saving = true;
    
    // Using the new assign-client-role API for each selected role
    const assignments = this.newRolesToAssign.map(roleName => {
      const request = {
        userId: this.selectedUser.id,
        clientName: this.selectedClient,
        roleName: roleName,
        region: this.selectedUser.region || Region.INDIA
      };
      return this.apiService.assignClientRole(request);
    });

    // We can use forkJoin if we want to wait for all, but for simplicity let's do them
    // and just refresh when done. For better UX, we'd handle individual errors.
    forkJoin(assignments).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Roles Assigned',
          detail: 'New roles have been added successfully'
        });
        this.newRolesToAssign = [];
        this.loadUserRoles();
        this.saving = false;
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Failed',
          detail: err.error?.detail || 'Could not assign some roles'
        });
        this.saving = false;
      }
    });
  }

  removeRole(roleName: string, userToUpdate?: any): void {
    const user = userToUpdate || this.selectedUser;
    
    this.confirmationService.confirm({
      message: `Are you sure you want to remove role <b>${roleName}</b> from this user?`,
      header: 'Remove Role',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        const request = {
          client: this.selectedClient,
          roles: [roleName]
        };
        this.apiService.deleteRoles(user.id, request).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Removed',
              detail: 'Role removed successfully'
            });
            
            // Update local state in table if user is provided
            if (user.roles) {
              user.roles = user.roles.filter((r: string) => r !== roleName);
            }
            
            // Also refresh dialog state if it's the selected user
            if (this.selectedUser && this.selectedUser.id === user.id) {
              this.loadUserRoles();
            }
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: err.error?.detail || 'Failed to remove role'
            });
          }
        });
      }
    });
  }

  confirmDelete(user: any): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to delete user <b>${user.username}</b>?`,
      header: 'Delete Confirmation',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        this.deleteUser(user.id);
      }
    });
  }

  deleteUser(userId: string): void {
    this.apiService.deleteUser(userId).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Deleted',
          detail: 'User deleted successfully'
        });
        this.loadUsers();
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err.error?.detail || 'Failed to delete user'
        });
      }
    });
  }

  editUser(user: any): void {
    this.selectedUser = user;
    this.editUserData = {
      Username: user.username,
      Email: user.email,
      FirstName: user.firstName,
      LastName: user.lastName,
      Enabled: user.enabled
    };
    this.displayEditDialog = true;
  }

  saveUser(): void {
    this.saving = true;
    const formData = new FormData();
    formData.append('Username', this.editUserData.Username);
    formData.append('Email', this.editUserData.Email);
    formData.append('FirstName', this.editUserData.FirstName);
    formData.append('LastName', this.editUserData.LastName);
    formData.append('Enabled', this.editUserData.Enabled.toString());

    this.apiService.updateUser(this.selectedUser.id, formData).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'User updated successfully'
        });
        this.displayEditDialog = false;
        this.saving = false;
        this.loadUsers();
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err.error?.detail || 'Failed to update user'
        });
        this.saving = false;
      }
    });
  }

  toggleUserStatus(user: any): void {
    const originalStatus = !user.enabled; // The UI already toggled the value via ngModel
    const request = {
      userId: user.id,
      enabled: user.enabled
    };

    this.apiService.setUserStatus(request).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Status Updated',
          detail: `User ${user.username} is now ${user.enabled ? 'Enabled' : 'Disabled'}`
        });
      },
      error: (err) => {
        // Revert UI if API fails
        user.enabled = originalStatus;
        this.messageService.add({
          severity: 'error',
          summary: 'Update Failed',
          detail: err.error?.detail || 'Could not update user status'
        });
      }
    });
  }

  showAddDialog(): void {
    this.addUserData = {
      username: '',
      email: '',
      firstName: '',
      lastName: '',
      region: Region.INDIA,
      isActive: true,
      sendActivationEmail: true
    };
    this.displayAddDialog = true;
  }

  saveNewUser(): void {
    this.saving = true;
    this.apiService.addUser(this.addUserData).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'User added successfully'
        });
        this.displayAddDialog = false;
        this.saving = false;
        this.loadUsers();
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err.error?.detail || 'Failed to add user'
        });
        this.saving = false;
      }
    });
  }

  resendVerificationEmail(user: any): void {
    const request = {
      userId: user.id
    };
    this.apiService.resendVerification(request).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: `Verification email resent to ${user.email}`
        });
      },
      error: (err) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err.error?.detail || 'Failed to resend verification email'
        });
      }
    });
  }

  resetPassword(user: any): void {
    this.confirmationService.confirm({
      message: `Are you sure you want to reset password for <b>${user.username}</b> to default (Password@2026)?`,
      header: 'Reset Password Confirmation',
      icon: 'pi pi-key',
      acceptButtonStyleClass: 'p-button-warning p-button-text',
      rejectButtonStyleClass: 'p-button-text p-button-secondary',
      accept: () => {
        const request = {
          userId: user.id,
          newPassword: 'Password@2026'
        };
        this.apiService.changePassword(request).subscribe({
          next: () => {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Password reset to default successfully'
            });
          },
          error: (err) => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: err.error?.detail || 'Failed to reset password'
            });
          }
        });
      }
    });
  }

  showUserDetails(user: any): void {
    this.loading = true;
    console.log('Fetching details for user:', user.id);
    this.apiService.getUser(user.id).subscribe({
      next: (response) => {
        console.log('User details response:', response);
        // Handle both response.data and direct response
        this.userDetails = response.data || response;
        console.log('Assigned userDetails:', this.userDetails);
        this.displayDetailDialog = true;
        this.loading = false;
      },
      error: (err) => {
        console.error('Error fetching user details:', err);
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to fetch user details'
        });
        this.loading = false;
      }
    });
  }
}
